import React, { useCallback, useContext, useEffect, useState } from 'react'
import { View, StyleSheet, ScrollView, Platform } from 'react-native'
import { Text, Button, Spinner, useTheme } from '@ui-kitten/components'
import * as Clipboard from 'expo-clipboard'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { UserContext } from '../../global/UserProvider'
import { aiAssistantsApi, httpStatusOf } from '../../global/AiAssistants/aiAssistantsApi'
import {
  EXAMPLE_PROMPT_KEYS,
  availabilityMessageKey,
  claudeCodeCommand,
  reportsInfoFailure,
} from '../../global/AiAssistants/aiAssistantsView'

const CLAUDE_STEPS = ['claudeStep1', 'claudeStep2', 'claudeStep3', 'claudeStep4', 'claudeStep5', 'claudeStep6', 'claudeStep7']
const CHATGPT_STEPS = ['chatgptStep1', 'chatgptStep2', 'chatgptStep3', 'chatgptStep4', 'chatgptStep5', 'chatgptStep6']
const WRITE_STEPS = ['writeStep1', 'writeStep2', 'writeStep3', 'writeStep4']
const NEEDS = ['need_admin', 'need_org', 'need_claude', 'need_chatgpt']
const TROUBLE = ['trouble_admin', 'trouble_org', 'trouble_url', 'trouble_reconnect', 'trouble_scopes', 'trouble_rate', 'trouble_support']
const DISCONNECT = ['disconnect_atlas', 'disconnect_claude', 'disconnect_chatgpt', 'disconnect_admin']

/**
 * ATL-1694 — Settings → AI assistants: the merchant-facing guide to connecting Claude or ChatGPT to
 * Atlas. Plain language, step by step: what the assistant can read and write, what is needed, the
 * live status of this organization and the server address (from `/ai-assistants/info`, never
 * hardcoded — staging and production differ), setup for each assistant, a connection check,
 * copyable example requests, how a write is previewed and confirmed, troubleshooting, and how to
 * disconnect. The steps describe the shipped flow: the backend's OAuth authorize endpoint sends the
 * browser to the ConnectAi consent screen, and only drafts can be written.
 */
export default function AiAssistantsScreen({ navigation }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context] = useContext(UserContext)
  const [info, setInfo] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copiedKey, setCopiedKey] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      setInfo(await aiAssistantsApi.info())
    } catch (error) {
      const status = httpStatusOf(error)
      if (reportsInfoFailure(status)) Sentry.captureException(error, { tags: { feature: 'ai-assistants' } })
      // 404: a backend without this feature (rolled back, or not deployed yet) — "not enabled", not
      // "check your internet".
      setLoadError(t(status === 404 ? 'aiAssistants.unavailable_not_enabled' : 'aiAssistants.statusLoadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const copy = async (key, value) => {
    await Clipboard.setStringAsync(value)
    setCopiedKey(key)
  }

  const isAdmin = info ? info.isAdmin : context?.user?.role === 'admin'
  const card = [styles.card, { backgroundColor: theme['background-basic-color-1'] }]
  const mono = [styles.mono, { backgroundColor: theme['background-basic-color-2'] }]

  // Plain render helpers, not components: a component declared in this body would be a new type on
  // every render and remount its subtree.
  const section = (title, children, testID) => (
    <View style={card} testID={testID}>
      <Text category='s1' style={styles.gap}>{title}</Text>
      {children}
    </View>
  )

  const list = (keys, numbered) =>
    keys.map((key, index) => (
      <View key={key} style={styles.step}>
        <Text style={styles.stepNumber}>{numbered ? `${index + 1}.` : '•'}</Text>
        <Text style={styles.flex}>{t(`aiAssistants.${key}`)}</Text>
      </View>
    ))

  const copyButton = (copyKey, value, testID) => (
    <Button size='tiny' appearance='outline' onPress={() => copy(copyKey, value)} style={styles.copy} testID={testID}>
      {copiedKey === copyKey ? t('aiAssistants.copied') : t('aiAssistants.copy')}
    </Button>
  )

  const unavailableKey = availabilityMessageKey(info?.availability)

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content} testID='ai-assistants-screen'>
      {section(t('aiAssistants.whatTitle'), (
        <>
          <Text style={styles.gap}>{t('aiAssistants.whatText')}</Text>
          <Text category='s2' style={styles.gap}>{t('aiAssistants.canReadTitle')}</Text>
          <Text style={styles.gap}>{t('aiAssistants.canRead')}</Text>
          <Text category='s2' style={styles.gap}>{t('aiAssistants.canWriteTitle')}</Text>
          <Text>{t('aiAssistants.canWrite')}</Text>
        </>
      ))}

      {section(t('aiAssistants.needTitle'), list(NEEDS, false))}

      {section(t('aiAssistants.statusTitle'), (
        <>
          {loading && <Spinner size='small' />}
          {!loading && loadError && (
            <>
              <Text status='danger' style={styles.gap}>{loadError}</Text>
              <Button size='small' appearance='outline' onPress={load} style={styles.alignStart}>
                {t('aiAssistants.retry')}
              </Button>
            </>
          )}
          {!loading && info && (
            <>
              {unavailableKey ? (
                <Text status='warning' style={styles.gap} testID='ai-assistants-unavailable'>{t(unavailableKey)}</Text>
              ) : (
                <Text status='success' style={styles.gap} testID='ai-assistants-available'>{t('aiAssistants.status_available')}</Text>
              )}
              {!isAdmin && <Text status='warning'>{t('aiAssistants.status_notAdmin')}</Text>}
            </>
          )}
        </>
      ), 'ai-assistants-status')}

      {section(t('aiAssistants.serverTitle'), (
        <>
          <Text appearance='hint' style={styles.gap}>{t('aiAssistants.serverText')}</Text>
          {info && (
            <>
              <Text selectable style={mono} testID='ai-assistants-server-url'>{info.serverUrl}</Text>
              {copyButton('server', info.serverUrl, 'ai-assistants-copy-url')}
            </>
          )}
          {!info && !loading && <Text appearance='hint'>—</Text>}
        </>
      ), 'ai-assistants-server')}

      {section(t('aiAssistants.claudeTitle'), (
        <>
          {list(CLAUDE_STEPS, true)}
          <Text appearance='hint' category='c1' style={styles.topGap}>{t('aiAssistants.claudeTeam')}</Text>
          <Text category='s2' style={[styles.gap, styles.topGap]}>{t('aiAssistants.claudeCodeTitle')}</Text>
          <Text style={styles.gap}>{t('aiAssistants.claudeCodeText')}</Text>
          {info && (
            <>
              <Text selectable style={mono}>{claudeCodeCommand(info.serverUrl)}</Text>
              {copyButton('claudeCode', claudeCodeCommand(info.serverUrl))}
            </>
          )}
        </>
      ), 'ai-assistants-claude')}

      {section(t('aiAssistants.chatgptTitle'), (
        <>
          {list(CHATGPT_STEPS, true)}
          <Text appearance='hint' category='c1' style={styles.topGap}>{t('aiAssistants.chatgptNote')}</Text>
        </>
      ), 'ai-assistants-chatgpt')}

      {section(t('aiAssistants.checkTitle'), <Text>{t('aiAssistants.checkText')}</Text>)}

      {section(t('aiAssistants.examplesTitle'), (
        <>
          <Text appearance='hint' style={styles.gap}>{t('aiAssistants.examplesText')}</Text>
          {EXAMPLE_PROMPT_KEYS.map((key) => (
            <View key={key} style={[styles.example, { backgroundColor: theme['background-basic-color-2'] }]}>
              <Text selectable>{t(key)}</Text>
              {copyButton(key, t(key))}
            </View>
          ))}
        </>
      ), 'ai-assistants-examples')}

      {section(t('aiAssistants.writeTitle'), (
        <>
          {list(WRITE_STEPS, true)}
          <Text appearance='hint' category='c1' style={styles.topGap}>{t('aiAssistants.writeNote')}</Text>
          <Text appearance='hint' category='c1' style={styles.topGap}>{t('aiAssistants.writeScopesNote')}</Text>
        </>
      ))}

      {section(t('aiAssistants.troubleTitle'), list(TROUBLE, false))}

      {section(t('aiAssistants.disconnectTitle'), (
        <>
          {list(DISCONNECT, false)}
          <Text appearance='hint' category='c1' style={styles.topGap}>{t('aiAssistants.disconnectNote')}</Text>
          <View style={styles.row}>
            {isAdmin && (
              <Button size='small' appearance='outline' onPress={() => navigation.navigate('ApiTokens')} style={styles.rowButton}>
                {t('aiAssistants.openTokens')}
              </Button>
            )}
            {info?.availability.available && (
              <Button size='small' appearance='outline' onPress={() => navigation.navigate('AgentDrafts')} style={styles.rowButton}>
                {t('aiAssistants.openDrafts')}
              </Button>
            )}
          </View>
        </>
      ))}
      <View style={styles.bottomSpacer} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  bottomSpacer: { height: 50 },
  container: { flex: 1, padding: 20 },
  // Room below the last line for the app-wide floating help button, which otherwise covers it.
  content: { paddingBottom: 96 },
  card: { borderRadius: 10, padding: 15, marginTop: 10 },
  gap: { marginBottom: 8 },
  topGap: { marginTop: 8 },
  flex: { flex: 1 },
  step: { flexDirection: 'row', marginBottom: 6 },
  stepNumber: { width: 22 },
  row: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 12 },
  rowButton: { marginRight: 10, marginBottom: 6 },
  alignStart: { alignSelf: 'flex-start' },
  copy: { alignSelf: 'flex-start', marginTop: 6 },
  mono: {
    padding: 10,
    borderRadius: 6,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  example: { borderRadius: 6, padding: 10, marginBottom: 8 },
})
